import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getWaitlist, updateWaitlistEntry, deleteWaitlistEntry } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { openWhatsApp } from './patient-profile/patientProfileHelpers';

// Panel de lista de espera (standby) de un médico puntual — pacientes que
// quieren un turno antes de su fecha asignada. Sin notificación automática:
// recepción contacta manualmente cuando se libera un hueco.
export default function WaitlistPanel({ open, onClose, doctorId, doctorName }) {
  const notify = useNotification();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    getWaitlist({ doctor_id: doctorId, status: 'activo' })
      .then(r => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [doctorId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleResolve = async (entry) => {
    try {
      await updateWaitlistEntry(entry.id, { status: 'resuelto' });
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      notify('Marcado como resuelto', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  const handleRemove = async (entry) => {
    try {
      await deleteWaitlistEntry(entry.id);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      notify('Quitado de la lista de espera', 'info');
    } catch (err) {
      notify(err.userMessage || 'Error', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          Lista de espera
          {doctorName && (
            <Typography variant="body2" color="text.secondary">{doctorName}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pacientes que quieren un turno antes de su fecha asignada. Se contacta manualmente
          cuando se libera un hueco (por ejemplo, al cancelar un turno) — no hay aviso automático.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : entries.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
            Nadie en lista de espera para este médico. Para agregar un paciente, abrí su perfil
            → tab "Turnos" → "Agregar a lista de espera".
          </Typography>
        ) : (
          <Stack spacing={1}>
            {entries.map(entry => (
              <Box key={entry.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5,
                border: '1px solid', borderColor: 'divider',
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{entry.patient?.name}</Typography>
                  {entry.notes && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {entry.notes}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled">
                    Anotado el {new Date(entry.created_at + (entry.created_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString('es-AR')}
                  </Typography>
                </Box>
                {entry.patient?.phone && (
                  <Tooltip title="Contactar por WhatsApp">
                    <IconButton size="small" onClick={() => openWhatsApp(entry.patient.phone)}>
                      <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Marcar como resuelto (ya consiguió turno)">
                  <IconButton size="small" onClick={() => handleResolve(entry)}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Quitar de la lista">
                  <IconButton size="small" onClick={() => handleRemove(entry)}>
                    <DeleteIcon sx={{ fontSize: 18, color: 'error.main' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
